var _a, _b, _c;
import React, { useMemo, useState } from 'react';
import { apis, hooks, models } from '@vote4dance/judging-sdk';
import { Alert, App, Button, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tabs, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, LinkOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
var Api = apis.Api;
var Stream = hooks.streams.Stream;
const SponsorItemsSource = (_a = models.sponsors) === null || _a === void 0 ? void 0 : _a.SponsorItemsSource;
const SponsorItemClassesSource = (_b = models.sponsors) === null || _b === void 0 ? void 0 : _b.SponsorItemClassesSource;
const SponsorPurchasesSource = (_c = models.sponsors) === null || _c === void 0 ? void 0 : _c.SponsorPurchasesSource;
var ClassItem = models.classes.ClassItem;
var ClassesSource = models.classes.ClassesSource;
import { HeaderContainer, BodyContainer } from '../../../../base-react/components/Container';
import { _T } from '../../../../base-react/src/Localization';
import { useCompetition } from '../../../../base-react/hooks/Competition';
import ImageUpload from '../../../../base-react/components/ImageUpload';
import { ButtonConfirm } from '../../../../base-react/components/ButtonConfirm';
import { SortableList, SortableListItem } from '../../../components/SortableList';
import { Loading } from '../../../../base-react/components/Loading';
import { getUrl } from '../../../../base-react/scripts/cdn';
import StripeConnectAccount, { isCompetitionStripeConnected } from '../../../components/StripeConnectAccount';
import { SPONSOR_SLOT_TEMPLATES } from './slotCatalog';
const { Title, Text } = Typography;
const { TextArea } = Input;
const formatMajorMoney = (amount, currency) => {
    const numeric = Number(amount || 0);
    const normalized = (currency || 'sek').toUpperCase();
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: normalized
        }).format(numeric);
    }
    catch (err) {
        return `${numeric} ${normalized}`;
    }
};
const CLASS_SPONSOR_SLOT_ID = 'class-sponsor';
const STANDARD_SEED_SLOT_TEMPLATES = SPONSOR_SLOT_TEMPLATES.filter((slot) => slot.id !== CLASS_SPONSOR_SLOT_ID);
const normalizeSponsorPurchaseAmount = (purchase, offer) => {
    const amount = Number(purchase.sponsor_purchase_amount_total || 0);
    const offerAmount = Number((offer === null || offer === void 0 ? void 0 : offer.sponsor_item_unit_amount) || 0);
    if (offerAmount > 0) {
        if (amount === offerAmount) {
            return amount;
        }
        if (amount === offerAmount * 100) {
            return amount / 100;
        }
    }
    return amount;
};
const getMinimumAmountForCurrency = (currency) => ((currency || 'sek') === 'sek' ? 500 : 50);
const getErrorMessage = (err) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const fallback = _T('common.error');
    const genericErrors = new Set(['Error', 'ClientResponseError', 'AxiosError']);
    const normalizeErrorValue = (value) => {
        const matchedKey = value.match(/[a-z0-9]+(?:-[a-z0-9]+)+/i);
        return matchedKey ? matchedKey[0] : value;
    };
    const candidates = [
        typeof err === 'string' ? err : undefined,
        (_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.name,
        (_d = (_c = err === null || err === void 0 ? void 0 : err.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.message,
        (_e = err === null || err === void 0 ? void 0 : err.response) === null || _e === void 0 ? void 0 : _e.name,
        (_f = err === null || err === void 0 ? void 0 : err.response) === null || _f === void 0 ? void 0 : _f.message,
        (_g = err === null || err === void 0 ? void 0 : err.response) === null || _g === void 0 ? void 0 : _g.error,
        (_h = err === null || err === void 0 ? void 0 : err.data) === null || _h === void 0 ? void 0 : _h.name,
        (_j = err === null || err === void 0 ? void 0 : err.data) === null || _j === void 0 ? void 0 : _j.message,
        err === null || err === void 0 ? void 0 : err.name,
        err === null || err === void 0 ? void 0 : err.message,
        typeof (err === null || err === void 0 ? void 0 : err.toString) === 'function' ? err.toString() : undefined
    ].filter((entry) => typeof entry === 'string' && entry.trim().length > 0);
    const translationKey = candidates
        .map(normalizeErrorValue)
        .find((entry) => !genericErrors.has(entry));
    if (!translationKey) {
        return fallback;
    }
    if (translationKey.includes(' ')) {
        return translationKey;
    }
    const translated = _T(translationKey);
    return translated || fallback;
};
const buildPublicSponsorSiteUrl = (competitionId) => (`${window.location.origin}/public/#event/${competitionId}/sponsors`);
const buildPictureUrl = (imageId) => (imageId ? getUrl(`pics/${imageId}/small`) : undefined);
const stripePricingUrl = 'https://stripe.com/pricing';
const normalizeSlotIds = (input) => {
    const parsed = (() => {
        if (!input) {
            return [];
        }
        if (Array.isArray(input)) {
            return input;
        }
        const stringValue = String(input).trim();
        if (!stringValue) {
            return [];
        }
        try {
            const jsonValue = JSON.parse(stringValue);
            if (Array.isArray(jsonValue)) {
                return jsonValue;
            }
        }
        catch {
            // legacy comma-separated support
        }
        return stringValue.split(',').map((value) => value.trim()).filter(Boolean);
    })();
    return Array.from(new Set(parsed
        .map((value) => String(value || '').trim())
        .filter(Boolean)));
};
const normalizeIds = (input) => {
    if (!Array.isArray(input)) {
        return [];
    }
    return Array.from(new Set(input
        .map((value) => String(value || '').trim())
        .filter(Boolean)));
};
export const Sponsors = () => {
    const { event_id } = useParams();
    const [competition, , , loadingCompetition, , canEdit] = useCompetition(event_id);
    const [items, loadingItems] = Stream(event_id ? SponsorItemsSource === null || SponsorItemsSource === void 0 ? void 0 : SponsorItemsSource(event_id) : undefined);
    const [itemClasses, loadingItemClasses] = Stream(event_id ? SponsorItemClassesSource === null || SponsorItemClassesSource === void 0 ? void 0 : SponsorItemClassesSource(event_id) : undefined);
    const [classes, loadingClasses] = Stream(event_id ? ClassesSource(event_id) : undefined);
    const [purchases, loadingPurchases] = Stream(event_id ? SponsorPurchasesSource === null || SponsorPurchasesSource === void 0 ? void 0 : SponsorPurchasesSource(event_id) : undefined);
    if (!event_id) {
        return null;
    }
    if (loadingCompetition || loadingItems || loadingItemClasses || loadingClasses || loadingPurchases) {
        return React.createElement(Loading, null);
    }
    const tabs = [
        {
            key: 'offers',
            label: _T('sponsors.tabs.offers'),
            children: (React.createElement(SponsorOffersTab, { competitionId: event_id, competition: competition, items: items || [], itemClasses: itemClasses || [], classes: classes || [], purchases: purchases || [], canEdit: canEdit }))
        },
        {
            key: 'purchases',
            label: _T('sponsors.tabs.purchases'),
            children: (React.createElement(SponsorPurchasesTab, { competitionId: event_id, purchases: purchases || [], items: items || [], canEdit: canEdit }))
        }
    ];
    return (React.createElement(React.Fragment, null,
        React.createElement(HeaderContainer, null,
            React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
                React.createElement(Typography, null,
                    React.createElement(Title, null, _T('sponsors.title'))),
                React.createElement(Space, null,
                    React.createElement(Button, { icon: React.createElement(LinkOutlined, null), href: buildPublicSponsorSiteUrl(event_id), target: "_blank" }, _T('sponsors.public-site'))))),
        React.createElement(BodyContainer, null,
            React.createElement(Tabs, { items: tabs }))));
};
const SponsorOffersTab = ({ competitionId, competition, items, itemClasses, classes, purchases, canEdit }) => {
    var _a, _b;
    const { notification } = App.useApp();
    const [offerEditor, setOfferEditor] = useState({ open: false });
    const [submitting, setSubmitting] = useState(false);
    const [creatingDefaults, setCreatingDefaults] = useState(false);
    const [restoringSlotId, setRestoringSlotId] = useState();
    const [restoringMissingItem, setRestoringMissingItem] = useState(false);
    const [form] = Form.useForm();
    const stripeConnected = isCompetitionStripeConnected(competition);
    const source = useMemo(() => SponsorItemsSource === null || SponsorItemsSource === void 0 ? void 0 : SponsorItemsSource(competitionId), [competitionId]);
    const classMap = useMemo(() => {
        const next = new Map();
        classes.forEach((item) => {
            next.set(String(item.class_id), item);
        });
        return next;
    }, [classes]);
    const sponsorClassesByItem = useMemo(() => {
        const next = new Map();
        itemClasses.forEach((item) => {
            const sponsorItemId = String(item.sponsor_item_id);
            const classId = String(item.class_id);
            if (!next.has(sponsorItemId)) {
                next.set(sponsorItemId, []);
            }
            next.get(sponsorItemId).push(classId);
        });
        return next;
    }, [itemClasses]);
    const purchaseCountByItem = useMemo(() => {
        const next = new Map();
        purchases.forEach((item) => {
            const key = String(item.sponsor_item_id);
            next.set(key, Number(next.get(key) || 0) + 1);
        });
        return next;
    }, [purchases]);
    const slotTemplatesById = useMemo(() => {
        const next = new Map();
        SPONSOR_SLOT_TEMPLATES.forEach((template) => next.set(template.id, template));
        return next;
    }, []);
    const slotIdsByOfferId = useMemo(() => {
        const next = new Map();
        items.forEach((item) => {
            const offerId = String(item.sponsor_item_id || '');
            if (!offerId) {
                return;
            }
            next.set(offerId, normalizeSlotIds(item.sponsor_item_placements));
        });
        return next;
    }, [items]);
    const getOfferSlotId = (item) => {
        if (!item) {
            return undefined;
        }
        if (item.sponsor_item_id) {
            return (slotIdsByOfferId.get(String(item.sponsor_item_id)) || [])[0];
        }
        return normalizeSlotIds(item.sponsor_item_placements)[0];
    };
    const existingSlotIds = useMemo(() => {
        const next = new Set();
        slotIdsByOfferId.forEach((slotIds) => {
            slotIds.forEach((slotId) => next.add(slotId));
        });
        return next;
    }, [slotIdsByOfferId]);
    const missingSlotTemplates = useMemo(() => STANDARD_SEED_SLOT_TEMPLATES.filter((slot) => !existingSlotIds.has(slot.id)), [existingSlotIds]);
    const classSponsorSlotTemplate = useMemo(() => SPONSOR_SLOT_TEMPLATES.find((slot) => slot.id === CLASS_SPONSOR_SLOT_ID), []);
    const getDefaultOfferDraftForSlot = (slot) => {
        const baseAmount = getMinimumAmountForCurrency('sek');
        const index = SPONSOR_SLOT_TEMPLATES.findIndex((entry) => entry.id === slot.id);
        const maxIndex = SPONSOR_SLOT_TEMPLATES.length - 1;
        return {
            sponsor_item_title: getSlotTitle(slot),
            sponsor_item_description: getSlotDescription(slot),
            sponsor_item_type: slot.maxSponsorsPerClass ? 'class' : 'tier',
            sponsor_item_currency: 'sek',
            sponsor_item_unit_amount: baseAmount * (2 ** (maxIndex - Math.max(index, 0))),
            sponsor_item_quantity_total: slot.maxSponsorsPerClass || slot.maxSponsorsTotalForSlot || 1,
            sponsor_item_placements: JSON.stringify([slot.id]),
            sponsor_item_active: 1
        };
    };
    const openEdit = (item) => {
        form.setFieldsValue({
            sponsor_item_title: item.sponsor_item_title,
            sponsor_item_description: item.sponsor_item_description,
            sponsor_item_currency: (item.sponsor_item_currency || 'sek'),
            sponsor_item_unit_amount: Number(item.sponsor_item_unit_amount || 0),
            sponsor_item_quantity_total: Number(item.sponsor_item_quantity_total || 1),
            sponsor_item_active: Number(item.sponsor_item_active) === 1,
            class_ids: sponsorClassesByItem.get(String(item.sponsor_item_id)) || []
        });
        setOfferEditor({ open: true, item });
    };
    const openCreateForSlot = (slot) => {
        const item = getDefaultOfferDraftForSlot(slot);
        form.setFieldsValue({
            sponsor_item_title: item.sponsor_item_title,
            sponsor_item_description: item.sponsor_item_description,
            sponsor_item_currency: (item.sponsor_item_currency || 'sek'),
            sponsor_item_unit_amount: Number(item.sponsor_item_unit_amount || 0),
            sponsor_item_quantity_total: Number(item.sponsor_item_quantity_total || 1),
            sponsor_item_active: Number(item.sponsor_item_active) === 1,
            class_ids: []
        });
        setOfferEditor({ open: true, item });
    };
    const closeEditor = () => {
        setOfferEditor({ open: false });
        form.resetFields();
    };
    const onSaveOffer = async () => {
        var _a, _b, _c, _d, _e;
        if (!((_a = offerEditor.item) === null || _a === void 0 ? void 0 : _a.sponsor_item_id) && !stripeConnected) {
            notification.error({ title: _T('sponsors.error.stripe-not-connected') });
            return;
        }
        const values = await form.validateFields();
        const quantityTotal = Number(((_b = offerEditor.item) === null || _b === void 0 ? void 0 : _b.sponsor_item_quantity_total) || 1);
        const currentOfferId = String(((_c = offerEditor.item) === null || _c === void 0 ? void 0 : _c.sponsor_item_id) || '');
        const selectedSlotIds = normalizeSlotIds((_d = offerEditor.item) === null || _d === void 0 ? void 0 : _d.sponsor_item_placements);
        const isClassSlot = selectedSlotIds.some((slotId) => { var _a; return Boolean((_a = slotTemplatesById.get(slotId)) === null || _a === void 0 ? void 0 : _a.maxSponsorsPerClass); });
        const selectedClassIds = isClassSlot ? normalizeIds(values.class_ids) : [];
        for (const slotId of selectedSlotIds) {
            const slotTemplate = slotTemplatesById.get(slotId);
            if (!slotTemplate) {
                continue;
            }
            if (slotTemplate.maxSponsorsPerClass) {
                if (selectedClassIds.length === 0) {
                    notification.error({
                        title: _T('sponsors.error.slot-classes-required', {
                            slot: getSlotTitle(slotTemplate)
                        })
                    });
                    return;
                }
                for (const classId of selectedClassIds) {
                    let nextSponsorsTotalForClass = quantityTotal;
                    items.forEach((item) => {
                        const offerId = String(item.sponsor_item_id || '');
                        if (!offerId || offerId === currentOfferId) {
                            return;
                        }
                        const existingSlotIds = slotIdsByOfferId.get(offerId) || [];
                        if (!existingSlotIds.includes(slotId)) {
                            return;
                        }
                        const existingClassIds = sponsorClassesByItem.get(offerId) || [];
                        if (!existingClassIds.includes(classId)) {
                            return;
                        }
                        nextSponsorsTotalForClass += Number(item.sponsor_item_quantity_total || 0);
                    });
                    if (nextSponsorsTotalForClass > slotTemplate.maxSponsorsPerClass) {
                        notification.error({
                            title: _T('sponsors.error.slot-sponsor-limit-per-class', {
                                slot: getSlotTitle(slotTemplate),
                                limit: String(slotTemplate.maxSponsorsPerClass),
                                class: getClassLabelById(classId)
                            })
                        });
                        return;
                    }
                }
            }
            let nextSponsorsTotalForSlot = quantityTotal;
            items.forEach((item) => {
                const offerId = String(item.sponsor_item_id || '');
                if (!offerId || offerId === currentOfferId) {
                    return;
                }
                const existingSlotIds = slotIdsByOfferId.get(offerId) || [];
                if (!existingSlotIds.includes(slotId)) {
                    return;
                }
                nextSponsorsTotalForSlot += Number(item.sponsor_item_quantity_total || 0);
            });
            if (slotTemplate.maxSponsorsTotalForSlot &&
                nextSponsorsTotalForSlot > slotTemplate.maxSponsorsTotalForSlot) {
                notification.error({
                    title: _T('sponsors.error.slot-sponsor-limit', {
                        slot: getSlotTitle(slotTemplate),
                        limit: String(slotTemplate.maxSponsorsTotalForSlot)
                    })
                });
                return;
            }
        }
        const payload = {
            sponsor_item_title: values.sponsor_item_title,
            sponsor_item_description: values.sponsor_item_description || null,
            sponsor_item_type: isClassSlot ? 'class' : 'tier',
            sponsor_item_currency: values.sponsor_item_currency,
            sponsor_item_unit_amount: values.sponsor_item_unit_amount,
            sponsor_item_quantity_total: quantityTotal,
            sponsor_item_placements: JSON.stringify(selectedSlotIds),
            sponsor_item_active: values.sponsor_item_active ? 1 : 0,
            class_ids: isClassSlot ? (values.class_ids || []) : []
        };
        setSubmitting(true);
        try {
            if ((_e = offerEditor.item) === null || _e === void 0 ? void 0 : _e.sponsor_item_id) {
                await Api.update(`${source.url}/${offerEditor.item.sponsor_item_id}`, payload);
                notification.success({
                    title: _T('sponsors.notifications.offer-updated')
                });
            }
            else {
                await Api.create(source.url, payload);
                notification.success({
                    title: _T('sponsors.notifications.offer-created')
                });
            }
            closeEditor();
        }
        catch (err) {
            notification.error({ title: getErrorMessage(err) });
        }
        setSubmitting(false);
    };
    const getSlotTitle = (slot) => _T(slot.titleKey);
    const getSlotDescription = (slot) => _T(slot.descriptionKey);
    const createDefaultOfferForSlot = async (slot) => {
        const draft = getDefaultOfferDraftForSlot(slot);
        await Api.create(source.url, {
            sponsor_item_title: draft.sponsor_item_title,
            sponsor_item_description: draft.sponsor_item_description,
            sponsor_item_type: draft.sponsor_item_type,
            sponsor_item_currency: draft.sponsor_item_currency,
            sponsor_item_unit_amount: draft.sponsor_item_unit_amount,
            sponsor_item_quantity_total: draft.sponsor_item_quantity_total,
            sponsor_item_placements: draft.sponsor_item_placements,
            sponsor_item_active: draft.sponsor_item_active,
            class_ids: []
        });
    };
    const createDefaultOffers = async () => {
        if (!stripeConnected) {
            notification.error({ title: _T('sponsors.error.stripe-not-connected') });
            return;
        }
        if (items.length > 0) {
            return;
        }
        setCreatingDefaults(true);
        try {
            for (const slot of STANDARD_SEED_SLOT_TEMPLATES) {
                await createDefaultOfferForSlot(slot);
            }
            notification.success({
                title: _T('sponsors.notifications.offer-defaults-created')
            });
        }
        catch (err) {
            notification.error({ title: getErrorMessage(err) });
        }
        setCreatingDefaults(false);
    };
    const restoreMissingDefaultOffer = async () => {
        if (!stripeConnected) {
            notification.error({ title: _T('sponsors.error.stripe-not-connected') });
            return;
        }
        const slot = missingSlotTemplates.find((entry) => entry.id === restoringSlotId);
        if (!slot) {
            return;
        }
        setRestoringMissingItem(true);
        try {
            await createDefaultOfferForSlot(slot);
            notification.success({
                title: _T('sponsors.notifications.offer-created')
            });
            setRestoringSlotId(undefined);
        }
        catch (err) {
            notification.error({ title: getErrorMessage(err) });
        }
        setRestoringMissingItem(false);
    };
    const createClassSponsorOffer = async () => {
        if (!classSponsorSlotTemplate) {
            return;
        }
        openCreateForSlot(classSponsorSlotTemplate);
    };
    const onDeleteOffer = async (item, done) => {
        try {
            await Api.destroy(`${source.url}/${item.sponsor_item_id}`);
            notification.success({
                title: _T('sponsors.notifications.offer-deleted')
            });
        }
        catch (err) {
            notification.error({ title: getErrorMessage(err) });
        }
        done();
    };
    const onSorted = async (list) => {
        const ids = list
            .map((entry) => entry.sponsor_item_id)
            .filter((entry) => Boolean(entry));
        try {
            await Api.call(source.url, 'set-order', ids);
        }
        catch (err) {
            notification.error({ title: getErrorMessage(err) });
        }
    };
    const onSetPicture = async (item, picture) => {
        if (!item.sponsor_item_id) {
            return;
        }
        try {
            await Api.call(`${source.url}/${item.sponsor_item_id}`, 'set-picture', { picture });
            notification.success({ title: _T('sponsors.notifications.offer-picture') });
        }
        catch (err) {
            notification.error({ title: getErrorMessage(err) });
        }
    };
    const classOptions = classes.map((item) => ({
        label: ClassItem.getClassLabel(item),
        value: item.class_id
    }));
    const getClassLabelById = (classId) => {
        const classItem = classMap.get(classId);
        return classItem ? ClassItem.getClassLabel(classItem) : classId;
    };
    const isEditorClassSlot = Boolean(offerEditor.item && ((_a = slotTemplatesById.get(getOfferSlotId(offerEditor.item) || '')) === null || _a === void 0 ? void 0 : _a.maxSponsorsPerClass));
    const renderOffer = (item) => {
        const itemId = String(item.sponsor_item_id || '');
        const linkedClassIds = sponsorClassesByItem.get(itemId) || [];
        const linkedClasses = linkedClassIds
            .map((classId) => classMap.get(classId))
            .filter(Boolean);
        const linkedSlotIds = slotIdsByOfferId.get(itemId) || [];
        const soldCount = Number(purchaseCountByItem.get(itemId) || 0);
        const quantityTotal = Number(item.sponsor_item_quantity_total || 0);
        const soldOut = soldCount >= quantityTotal;
        return (React.createElement(SortableListItem, { item: item, idAttribute: "sponsor_item_id", disabled: !canEdit, element: () => (React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
                React.createElement(Space, { wrap: true },
                    React.createElement(Text, { strong: true }, item.sponsor_item_title),
                    Number(item.sponsor_item_active) !== 1 && React.createElement(Tag, { color: "default" }, _T('sponsors.status.inactive')),
                    soldOut && React.createElement(Tag, { color: "red" }, _T('sponsors.status.sold-out'))),
                item.sponsor_item_description && React.createElement(Text, { type: "secondary" }, item.sponsor_item_description),
                React.createElement(Space, { wrap: true },
                    React.createElement(Text, null,
                        _T('sponsors.offer.price'),
                        ": ",
                        formatMajorMoney(item.sponsor_item_unit_amount, item.sponsor_item_currency)),
                    React.createElement(Text, null,
                        _T('sponsors.offer.currency'),
                        ": ",
                        (item.sponsor_item_currency || '').toUpperCase())),
                linkedSlotIds.some((slotId) => { var _a; return Boolean((_a = slotTemplatesById.get(slotId)) === null || _a === void 0 ? void 0 : _a.maxSponsorsPerClass); }) && linkedClasses.length > 0 && (React.createElement(Space, { wrap: true },
                    React.createElement(Text, null,
                        _T('sponsors.offer.classes'),
                        ":"),
                    linkedClasses.map((classItem) => (React.createElement(Tag, { key: classItem.class_id }, ClassItem.getClassLabel(classItem)))))),
                item.sponsor_item_id && (React.createElement(Space, { wrap: true },
                    React.createElement(Button, { icon: React.createElement(EditOutlined, null), onClick: () => openEdit(item), disabled: !canEdit }, _T('common.edit')),
                    React.createElement(ButtonConfirm, { title: _T('sponsors.offer.delete'), description: _T('sponsors.offer.delete-confirm'), onConfirm: (done) => onDeleteOffer(item, done), disabled: !canEdit, buttonProps: {
                            icon: React.createElement(DeleteOutlined, null),
                            danger: true,
                            label: _T('sponsors.offer.delete')
                        } }))))) }));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
            React.createElement(Alert, { type: "warning", showIcon: true, title: _T('sponsors.offer.policy.title'), description: (React.createElement(Space, { orientation: "vertical", size: 0 },
                    React.createElement(Text, null, _T('sponsors.offer.policy.purchase-only')),
                    React.createElement(Text, null, _T('sponsors.offer.policy.no-external')),
                    React.createElement(Text, null,
                        _T('sponsors.offer.policy.fees'),
                        ' ',
                        React.createElement(Link, { to: stripePricingUrl, target: "_blank" }, "Stripe")))) }),
            React.createElement(StripeConnectAccount, { competition: competition, disabled: !canEdit, showDescription: false }),
            React.createElement(Text, { type: "secondary" }, _T('sponsors.offer.vat-excluded')),
            items.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement(SortableList, { contextId: `sponsor-offers-${competitionId}`, items: items, idAttribute: "sponsor_item_id", renderItem: renderOffer, onSorted: onSorted, disabled: !canEdit }),
                missingSlotTemplates.length > 0 && (React.createElement(Space, { wrap: true, style: { width: '100%' } },
                    React.createElement(Select, { style: { minWidth: 280 }, value: restoringSlotId, onChange: setRestoringSlotId, placeholder: _T('sponsors.offer.restore-placeholder'), options: missingSlotTemplates.map((slot) => ({
                            value: slot.id,
                            label: getSlotTitle(slot)
                        })) }),
                    React.createElement(Button, { type: "primary", onClick: restoreMissingDefaultOffer, disabled: !canEdit || !stripeConnected || !restoringSlotId, loading: restoringMissingItem }, _T('sponsors.offer.restore-button')))))),
            items.length === 0 && (React.createElement(Alert, { type: "info", showIcon: true, title: _T('sponsors.offer.seed-title'), description: (React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
                    React.createElement(Text, null, _T('sponsors.offer.seed-description')),
                    React.createElement(Button, { type: "primary", size: "large", block: true, onClick: createDefaultOffers, disabled: !canEdit || !stripeConnected, loading: creatingDefaults }, _T('sponsors.offer.seed-button')))) })),
            classSponsorSlotTemplate && (React.createElement(Alert, { type: "info", showIcon: true, title: _T('sponsors.offer.class-sponsor-title'), description: (React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
                    React.createElement(Text, null, _T('sponsors.offer.class-sponsor-description')),
                    React.createElement(Button, { type: "default", onClick: createClassSponsorOffer, disabled: !canEdit || !stripeConnected }, _T('sponsors.offer.class-sponsor-button')))) }))),
        React.createElement(Modal, { open: offerEditor.open, title: _T('sponsors.offer.edit'), onCancel: closeEditor, onOk: onSaveOffer, okButtonProps: { loading: submitting, disabled: !canEdit }, cancelButtonProps: { disabled: submitting } },
            React.createElement(Alert, { style: { marginBottom: 16 }, type: "warning", showIcon: true, title: _T('sponsors.offer.policy.title'), description: (React.createElement(Space, { orientation: "vertical", size: 0 },
                    React.createElement(Text, null, _T('sponsors.offer.policy.purchase-only')),
                    React.createElement(Text, null, _T('sponsors.offer.policy.no-external')),
                    React.createElement(Text, null,
                        _T('sponsors.offer.policy.fees'),
                        ' ',
                        React.createElement(Link, { to: stripePricingUrl, target: "_blank" }, "Stripe")))) }),
            React.createElement(Form, { layout: "vertical", form: form, initialValues: {
                    sponsor_item_currency: 'sek',
                    sponsor_item_unit_amount: 0,
                    sponsor_item_active: true,
                    class_ids: []
                } },
                React.createElement(Form.Item, { name: "sponsor_item_title", label: _T('sponsors.offer.fields.title'), rules: [{ required: true, message: _T('common.required') }] },
                    React.createElement(Input, { maxLength: 128 })),
                React.createElement(Form.Item, { name: "sponsor_item_description", label: _T('sponsors.offer.fields.description') },
                    React.createElement(TextArea, { rows: 3 })),
                isEditorClassSlot && (React.createElement(Form.Item, { name: "class_ids", label: _T('sponsors.offer.fields.classes'), rules: [{ required: true, message: _T('common.required') }] },
                    React.createElement(Select, { mode: "multiple", options: classOptions }))),
                React.createElement(Space, { style: { width: '100%' }, size: 12 },
                    React.createElement(Form.Item, { style: { flex: 1 }, name: "sponsor_item_currency", label: _T('sponsors.offer.fields.currency'), rules: [{ required: true, message: _T('common.required') }] },
                        React.createElement(Select, { options: [
                                { value: 'sek', label: 'SEK' },
                                { value: 'eur', label: 'EUR' },
                                { value: 'usd', label: 'USD' },
                                { value: 'nok', label: 'NOK' }
                            ] })),
                    React.createElement(Form.Item, { noStyle: true, shouldUpdate: (prev, next) => prev.sponsor_item_currency !== next.sponsor_item_currency }, ({ getFieldValue }) => {
                        const currency = String(getFieldValue('sponsor_item_currency') || 'sek').toLowerCase();
                        const minimum = getMinimumAmountForCurrency(currency);
                        return (React.createElement(Form.Item, { style: { flex: 1 }, name: "sponsor_item_unit_amount", label: _T('sponsors.offer.fields.amount'), rules: [
                                { required: true, message: _T('common.required') },
                                {
                                    type: 'number',
                                    min: minimum,
                                    message: `${_T('common.min')}: ${minimum} ${currency.toUpperCase()}`
                                }
                            ] },
                            React.createElement(InputNumber, { style: { width: '100%' }, min: minimum, step: 10, precision: 2 })));
                    })),
                React.createElement(Form.Item, { noStyle: true, shouldUpdate: (prev, next) => prev.sponsor_item_currency !== next.sponsor_item_currency }, ({ getFieldValue }) => {
                    const currency = String(getFieldValue('sponsor_item_currency') || 'sek').toLowerCase();
                    const minimum = getMinimumAmountForCurrency(currency);
                    return (React.createElement(Space, { orientation: "vertical", size: 0 },
                        React.createElement(Text, { type: "secondary" }, _T('sponsors.offer.fields.amount-minimum', {
                            value: `${minimum} ${currency.toUpperCase()}`
                        })),
                        React.createElement(Text, { type: "secondary" }, _T('sponsors.offer.vat-excluded'))));
                }),
                React.createElement(Form.Item, { name: "sponsor_item_quantity_total", label: _T('sponsors.offer.fields.quantity') },
                    React.createElement(InputNumber, { style: { width: '100%' }, disabled: true })),
                React.createElement(Form.Item, { name: "sponsor_item_active", valuePropName: "checked", label: _T('sponsors.offer.fields.active') },
                    React.createElement(Switch, null))),
            React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
                React.createElement(Text, { strong: true }, _T('sponsors.offer.fields.image')),
                ((_b = offerEditor.item) === null || _b === void 0 ? void 0 : _b.sponsor_item_id) ? (React.createElement(ImageUpload, { value: offerEditor.item.sponsor_item_image_id, onChange: (value) => onSetPicture(offerEditor.item, value), disabled: !canEdit || submitting })) : (React.createElement(Text, { type: "secondary" }, _T('sponsors.offer.fields.image-hint')))))));
};
const SponsorPurchasesTab = ({ competitionId, purchases, items, canEdit }) => {
    var _a;
    const { notification } = App.useApp();
    const [form] = Form.useForm();
    const [purchaseEditor, setPurchaseEditor] = useState({ open: false });
    const [savingPurchase, setSavingPurchase] = useState(false);
    const [purchaseImageId, setPurchaseImageId] = useState();
    const itemMap = useMemo(() => {
        const next = new Map();
        items.forEach((item) => {
            next.set(String(item.sponsor_item_id), item);
        });
        return next;
    }, [items]);
    const purchaseSource = useMemo(() => {
        var _a;
        return (((_a = purchaseEditor.purchase) === null || _a === void 0 ? void 0 : _a.sponsor_purchase_id)
            ? `${SponsorPurchasesSource(competitionId).url}/${purchaseEditor.purchase.sponsor_purchase_id}`
            : undefined);
    }, [competitionId, (_a = purchaseEditor.purchase) === null || _a === void 0 ? void 0 : _a.sponsor_purchase_id]);
    const amountSummary = useMemo(() => {
        const totalsByCurrency = new Map();
        purchases.forEach((purchase) => {
            const currency = String(purchase.sponsor_purchase_currency || 'sek').toLowerCase();
            const offer = itemMap.get(String(purchase.sponsor_item_id));
            const amount = normalizeSponsorPurchaseAmount(purchase, offer);
            totalsByCurrency.set(currency, Number(totalsByCurrency.get(currency) || 0) + amount);
        });
        return Array.from(totalsByCurrency.entries())
            .map(([currency, amount]) => formatMajorMoney(amount, currency))
            .join(' / ');
    }, [itemMap, purchases]);
    const openPurchaseEditor = (purchase) => {
        form.setFieldsValue({
            sponsor_purchase_company_name: purchase.sponsor_purchase_company_name,
            sponsor_purchase_contact_email: purchase.sponsor_purchase_contact_email,
            sponsor_purchase_company_link: purchase.sponsor_purchase_company_link
        });
        setPurchaseImageId(purchase.sponsor_purchase_company_image_id || undefined);
        setPurchaseEditor({ open: true, purchase });
    };
    const closePurchaseEditor = () => {
        setPurchaseEditor({ open: false });
        setPurchaseImageId(undefined);
        form.resetFields();
    };
    const onSavePurchase = async () => {
        if (!purchaseSource) {
            return;
        }
        const values = await form.validateFields();
        setSavingPurchase(true);
        try {
            await Api.update(purchaseSource, values);
            notification.success({
                title: _T('sponsors.notifications.purchase-updated')
            });
            closePurchaseEditor();
        }
        catch (err) {
            notification.error({ title: getErrorMessage(err) });
        }
        setSavingPurchase(false);
    };
    const onSetPurchasePicture = async (picture) => {
        if (!purchaseSource) {
            return;
        }
        try {
            const result = await Api.call(purchaseSource, 'set-picture', { picture });
            setPurchaseImageId((current) => (result === null || result === void 0 ? void 0 : result.sponsor_purchase_company_image_id) || current);
            notification.success({
                title: _T('sponsors.notifications.purchase-picture')
            });
        }
        catch (err) {
            notification.error({ title: getErrorMessage(err) });
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Table, { rowKey: (item) => String(item.sponsor_purchase_id || item.claim_token), dataSource: purchases, pagination: { pageSize: 20 }, summary: () => (React.createElement(Table.Summary, null,
                React.createElement(Table.Summary.Row, null,
                    React.createElement(Table.Summary.Cell, { index: 0, colSpan: 3 },
                        React.createElement(Text, { strong: true }, _T('common.total'))),
                    React.createElement(Table.Summary.Cell, { index: 3 },
                        React.createElement(Text, { strong: true }, amountSummary || '-')),
                    React.createElement(Table.Summary.Cell, { index: 4 })))), columns: [
                {
                    title: _T('sponsors.purchase.columns.company'),
                    key: 'company',
                    render: (_, item) => (React.createElement(Space, { orientation: "vertical", size: 0 },
                        React.createElement(Text, { strong: true }, item.sponsor_purchase_company_name),
                        React.createElement(Text, { type: "secondary" }, item.sponsor_purchase_contact_email),
                        item.sponsor_purchase_company_link && (React.createElement("a", { href: item.sponsor_purchase_company_link, target: "_blank", rel: "noreferrer" }, item.sponsor_purchase_company_link))))
                },
                {
                    title: _T('sponsors.purchase.columns.image'),
                    key: 'image',
                    width: 90,
                    render: (_, item) => (item.sponsor_purchase_company_image_id
                        ? React.createElement("img", { src: buildPictureUrl(item.sponsor_purchase_company_image_id), alt: item.sponsor_purchase_company_name || _T('sponsors.purchase.columns.image'), style: {
                                width: 48,
                                height: 48,
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid #f0f0f0'
                            } })
                        : React.createElement(Text, { type: "secondary" }, "-"))
                },
                {
                    title: _T('sponsors.purchase.columns.offer'),
                    key: 'offer',
                    render: (_, item) => {
                        const offer = itemMap.get(String(item.sponsor_item_id));
                        return React.createElement(Text, null, (offer === null || offer === void 0 ? void 0 : offer.sponsor_item_title) || item.sponsor_item_id);
                    }
                },
                {
                    title: _T('sponsors.purchase.columns.amount'),
                    key: 'amount',
                    render: (_, item) => {
                        const offer = itemMap.get(String(item.sponsor_item_id));
                        return formatMajorMoney(normalizeSponsorPurchaseAmount(item, offer), item.sponsor_purchase_currency);
                    }
                },
                {
                    title: _T('sponsors.purchase.columns.actions'),
                    key: 'actions',
                    render: (_, item) => (React.createElement(Space, { wrap: true },
                        React.createElement(Button, { icon: React.createElement(EditOutlined, null), onClick: () => openPurchaseEditor(item), disabled: !canEdit || !item.sponsor_purchase_id }, _T('sponsors.purchase.edit-info'))))
                }
            ] }),
        React.createElement(Modal, { open: purchaseEditor.open, title: _T('sponsors.purchase.edit-info'), onCancel: closePurchaseEditor, onOk: onSavePurchase, okText: _T('common.save'), confirmLoading: savingPurchase, destroyOnHidden: true },
            React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
                React.createElement(Form, { layout: "vertical", form: form },
                    React.createElement(Form.Item, { name: "sponsor_purchase_company_name", label: _T('sponsors.purchase.fields.company-name'), rules: [{ required: true, message: _T('common.required') }] },
                        React.createElement(Input, null)),
                    React.createElement(Form.Item, { name: "sponsor_purchase_contact_email", label: _T('sponsors.purchase.fields.contact-email'), rules: [
                            { required: true, message: _T('common.required') },
                            { type: 'email', message: _T('sponsors.purchase.fields.invalid-email') }
                        ] },
                        React.createElement(Input, { type: "email" })),
                    React.createElement(Form.Item, { name: "sponsor_purchase_company_link", label: _T('sponsors.purchase.fields.company-link') },
                        React.createElement(Input, null))),
                React.createElement(Space, { orientation: "vertical", size: "small", style: { width: '100%' } },
                    React.createElement(Text, { strong: true }, _T('sponsors.purchase.fields.image')),
                    React.createElement(ImageUpload, { value: purchaseImageId, onChange: onSetPurchasePicture }))))));
};
